/**
 * Per-project access: explicit grants, visibility and transfer (CLAUDE.md 11.3, 11.5).
 *
 * Kept apart from `workspaces.ts` because the scoping differs: these are keyed by
 * project, and a project can move between workspaces (11.5) while its members list
 * stays with it. Folding them together would have made the cache keys lie about that.
 *
 * The endpoints answer 204 rather than echoing the row, so every mutation here
 * invalidates rather than writing into the cache. Guessing the new list locally would
 * silently disagree with the server the first time it applied a rule we forgot --
 * a stale grant dropped, say.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/api/client'
import { queryKeys } from '@/api/hooks'
import type {
  Project,
  ProjectMembers,
  ProjectRole,
  ProjectVisibility,
  TransferResult,
} from '@/api/types'

export const projectMemberKeys = {
  members: (projectId: number) => ['project', projectId, 'members'] as const,
}

/**
 * The owner plus every explicit grant.
 *
 * Readable by anyone who can see the project: knowing who else can reach a
 * calculation is part of being able to work on it.
 */
export function useProjectMembers(projectId: number | null, enabled = true) {
  return useQuery({
    queryKey: projectMemberKeys.members(projectId ?? 0),
    queryFn: () => api.get<ProjectMembers>(`/projects/${projectId}/members`),
    enabled: projectId !== null && enabled,
  })
}

export function useAddProjectMember(projectId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { user_id: number; role: ProjectRole }) =>
      api.post<void>(`/projects/${projectId}/members`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectMemberKeys.members(projectId) })
    },
  })
}

export function useUpdateProjectMember(projectId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, role }: { userId: number; role: ProjectRole }) =>
      api.patch<void>(`/projects/${projectId}/members/${userId}`, { role }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectMemberKeys.members(projectId) })
    },
  })
}

export function useRemoveProjectMember(projectId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId: number) => api.delete<void>(`/projects/${projectId}/members/${userId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectMemberKeys.members(projectId) })
    },
  })
}

/**
 * Switch a project between `private` and `workspace` (11.3).
 *
 * The project row comes back with the caller's recomputed capabilities, so it is
 * written straight into the cache -- a widened visibility must not leave the page
 * still hiding the controls it now permits.
 */
export function useSetVisibility(projectId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (visibility: ProjectVisibility) =>
      api.patch<Project>(`/projects/${projectId}/visibility`, { visibility }),
    onSuccess: (project) => {
      queryClient.setQueryData(queryKeys.project(projectId), project)
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
  })
}

/**
 * Move a project to another workspace the caller belongs to (11.5).
 *
 * The response names everyone whose explicit grant was dropped, because they are not
 * members of the destination. That list is the point of the call returning anything
 * at all: silently dropping a colleague's access and leaving them to discover it is
 * not acceptable, so the caller must show it.
 */
export function useTransferProject(projectId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (targetWorkspaceId: number) =>
      api.post<TransferResult>(`/projects/${projectId}/transfer`, {
        target_workspace_id: targetWorkspaceId,
      }),
    onSuccess: (result) => {
      queryClient.setQueryData(queryKeys.project(projectId), result.project)
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
  })
}
